import React from "react";
import { cn } from "@/lib/utils";

/**
 * Button — Executive Redesign primitive.
 *
 * Variants:
 *   • primary    — filled accent (default)
 *   • secondary  — bordered on surface
 *   • ghost      — no fill, no border (icon-only actions, table row actions)
 *   • danger     — filled danger tone
 *
 * Sizes:  sm | md (default) | lg
 *
 * A11y: `<button>` element with `type="button"` default (so Return in
 * a form does NOT accidentally submit); `aria-busy` toggled when the
 * `loading` prop is true.
 *
 * Guardrails:
 *   • No gradient fills, no glow shadow, no bouncy hover scale.
 *   • Every interactive instance MUST carry `data-testid`.
 */

const BASE =
  "inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap select-none " +
  "border transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-color-background)] " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const VARIANTS = {
  primary:
    "bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] " +
    "text-[var(--ds-color-accent-ink)] border-[var(--ds-color-accent)]",
  secondary:
    "bg-[var(--ds-color-surface)] hover:bg-[var(--ds-color-surface-muted)] " +
    "text-[var(--ds-color-ink)] border-[var(--ds-color-border-strong)]",
  ghost:
    "bg-transparent hover:bg-[var(--ds-color-surface-muted)] " +
    "text-[var(--ds-color-ink)] border-transparent",
  danger:
    "bg-[var(--ds-color-danger)] hover:opacity-90 " +
    "text-[var(--ds-color-accent-ink)] border-[var(--ds-color-danger)]",
};

const SIZES = {
  sm: "h-8  px-3 text-[13px] rounded-[var(--ds-radius-sm)]",
  md: "h-10 px-4 text-[14px] rounded-[var(--ds-radius-md)]",
  lg: "h-12 px-6 text-[15px] rounded-[var(--ds-radius-md)]",
};

export const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    className,
    type = "button",
    loading = false,
    disabled = false,
    "data-testid": testId,
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-testid={testId}
      className={cn(
        BASE,
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        className
      )}
      style={{ transitionDuration: "var(--ds-duration-fast)" }}
      {...rest}
    >
      {children}
    </button>
  );
});

Button.displayName = "DSButton";

export default Button;
